import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { Link } from '@lululemon/b2b-ecom-pattern-library';
import { CALIFORNIA_PRIVACY_TEXT, CALIFORNIA_TRANSPARENCY_TEXT } from 'helpers/constants/common';
import { useFormat } from 'helpers/hooks/useFormat';
import { isNorthAmericaStore } from 'helpers/utils/countryHelper';
import { ACCOUNT } from 'helpers/utils/googleAnalyticsEvents/account';
import { FEATURE_FLAG_LIST, useFeatureFlags } from 'hooks';
import { useAnalytics } from 'hooks/useAnalytics';
import { useAccount } from 'frontastic/index';
import styles from './column.module.scss';
import { FooterColumnProps, LocaleSpecific } from './types';
import { filteredLocaleSpecificFooterData } from './utils';

export type ColumnProps = {
  column: FooterColumnProps;
  columnIndex: number;
};

export const Column = ({ column, columnIndex }: ColumnProps) => {
  const { account, logout } = useAccount();
  const activeStore = account?.activeStore;
  const { trackEvent, EVENT_CATEGORY } = useAnalytics();
  const { isFeatureEnabled } = useFeatureFlags();
  const isHideCaliforniaFooterLinkEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.HIDE_CALIFORNIA_FOOTER_LINK_INT);

  const { header, links } = column;

  // Filter out California-specific links for international users when feature flag is enabled.
  const filteredLinks =
    isHideCaliforniaFooterLinkEnabled && activeStore && !isNorthAmericaStore(activeStore)
      ? links.filter(
          (link) =>
            !link.name.toLowerCase().includes(CALIFORNIA_PRIVACY_TEXT) &&
            !link.name.toLowerCase().includes(CALIFORNIA_TRANSPARENCY_TEXT),
        )
      : links;

  const router = useRouter();
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });

  const handleLogout = () => {
    trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, ACCOUNT.FOOTER_MENU_ICON_CLICK('Sign Out'));
    logout().then(() => {
      trackEvent(EVENT_CATEGORY.APP_RESPONSE, ACCOUNT.FOOTER_MENU_SIGN_OUT_CLICK);
      router.push('/login');
    });
  };

  const filteredLocaleSpecificLinks = filteredLocaleSpecificFooterData(filteredLinks, router.locale as LocaleSpecific);

  const footerAccountMenuLinkHandler = (linkText = '') => {
    trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, ACCOUNT.FOOTER_MENU_ICON_CLICK(linkText));
  };

  return (
    <div>
      <div className={styles.header} data-testid="footer__column-header_test-id">
        {header}
      </div>

      <ul role="list" className={styles.listContainer}>
        {filteredLocaleSpecificLinks.map(({ reference, name }, i) => (
          <li key={i}>
            <Link
              className={styles.footerLinks}
              data-testid={`footer__column-link-${columnIndex}-${i}_test-id`}
              tag="div"
              variant="underlineAnimated"
            >
              <NextLink
                href={reference.type === 'link' ? reference.link : reference.pageFolder._url}
                onClick={columnIndex === 0 ? () => footerAccountMenuLinkHandler(name) : undefined}
                rel={reference.openInNewWindow ? 'noopener' : undefined}
                target={reference.openInNewWindow ? '_blank' : undefined}
              >
                {name}
              </NextLink>
            </Link>
          </li>
        ))}
        {columnIndex === 0 && (
          <li>
            <Link
              className={styles.footerLinks}
              data-testid={`footer__column-link-sign-out_test-id`}
              onClick={handleLogout}
              tag="button"
              variant="underlineAnimated"
            >
              {formatAccountMessage({ id: 'sign.out', defaultMessage: 'Sign Out' })}
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
};
